// Turns a change between two versions of a document template into something a person can read: one line per
// section added, removed, moved or changed (named by its heading), and the prompt as word-level redlines of just the
// paragraphs that changed, in full. Both sides are compared directly, so the same view works for an AI proposal and for
// "See my changes" / "See what changed in live" on a draft.

import { STYLES, sectionsFrom } from './documentSections';

const STYLE_LABELS = Object.fromEntries(STYLES.map((s) => [s.value, s.label]));
const MAX_TOKENS = 4000; // beyond this, show the old and new text whole instead of a word diff

export function styleLabel(style) {
  return STYLE_LABELS[style] || style;
}

function tokens(text) {
  return String(text || '').split(/(\s+)/).filter((t) => t !== '');
}

/** Word-level diff: [{ op: ' ' | '-' | '+', text }], with neighbouring parts of the same kind merged. */
export function wordDiff(before, after) {
  const a = tokens(before);
  const b = tokens(after);
  let parts;
  if (a.length * b.length > MAX_TOKENS * MAX_TOKENS / 4) {
    parts = [{ op: '-', text: String(before || '') }, { op: '+', text: String(after || '') }];
  } else {
    // Longest common subsequence on tokens.
    const lcs = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i -= 1) {
      for (let j = b.length - 1; j >= 0; j -= 1) {
        lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }
    parts = [];
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) { parts.push({ op: ' ', text: a[i] }); i += 1; j += 1; }
      else if (lcs[i + 1][j] >= lcs[i][j + 1]) { parts.push({ op: '-', text: a[i] }); i += 1; }
      else { parts.push({ op: '+', text: b[j] }); j += 1; }
    }
    while (i < a.length) { parts.push({ op: '-', text: a[i] }); i += 1; }
    while (j < b.length) { parts.push({ op: '+', text: b[j] }); j += 1; }
  }
  return group(parts);
}

// Neighbouring changes separated only by spaces read as one edit: "the" → "a short", not "the" → "a" + "short".
function group(parts) {
  const out = [];
  let removed = '';
  let added = '';
  const flush = () => {
    if (removed) out.push({ op: '-', text: removed });
    if (added) out.push({ op: '+', text: added });
    removed = '';
    added = '';
  };
  parts.forEach((p, k) => {
    const between = p.op === ' ' && /^\s+$/.test(p.text) && (removed || added)
      && parts.slice(k + 1).find((q) => !(q.op === ' ' && /^\s+$/.test(q.text)))?.op !== ' ';
    if (p.op === '-' || between) removed += p.text;
    if (p.op === '+' || between) added += p.text;
    if (p.op === ' ' && !between) {
      flush();
      const last = out[out.length - 1];
      if (last && last.op === ' ') last.text += p.text;
      else out.push({ ...p });
    }
  });
  flush();
  return out;
}

/** The two sides of a word diff, for showing Before and After next to each other. */
export function sides(parts) {
  return {
    before: parts.filter((p) => p.op !== '+'),
    after: parts.filter((p) => p.op !== '-'),
  };
}

function wordCount(text) {
  return tokens(text).filter((t) => !/^\s+$/.test(t)).length;
}

function title(section) {
  return section.label || section.key.replace(/_/g, ' ');
}

function excerpt(text, max = 160) {
  const s = String(text || '').trim();
  return s.length > max ? `${s.slice(0, max).trimEnd()}…` : s;
}

function fieldNames(section) {
  return section.fields.filter((f) => f.column !== false || section.style !== 'table').map((f) => f.name.replace(/_/g, ' '));
}

function sectionChanges(before, after) {
  const changes = [];
  if ((before.label || '') !== (after.label || '')) {
    changes.push({ what: 'heading', text: `Heading: “${before.label || 'none'}” → “${after.label || 'none'}”` });
  }
  if (before.style !== after.style) {
    changes.push({ what: 'style', text: `Shown as ${styleLabel(after.style)} (was ${styleLabel(before.style)})` });
  }
  if ((before.description || '') !== (after.description || '')) {
    changes.push({ what: 'instructions', text: 'Instructions changed', diff: wordDiff(before.description, after.description) });
  }
  const oldFields = Object.fromEntries(before.fields.map((f) => [f.name, f]));
  const newFields = Object.fromEntries(after.fields.map((f) => [f.name, f]));
  const added = after.fields.filter((f) => !oldFields[f.name]).map((f) => f.name.replace(/_/g, ' '));
  const removed = before.fields.filter((f) => !newFields[f.name]).map((f) => f.name.replace(/_/g, ' '));
  if (added.length) changes.push({ what: 'fields', text: `Added ${added.length === 1 ? 'field' : 'fields'}: ${added.join(', ')}` });
  if (removed.length) changes.push({ what: 'fields', text: `Removed ${removed.length === 1 ? 'field' : 'fields'}: ${removed.join(', ')}` });
  after.fields.forEach((f) => {
    const old = oldFields[f.name];
    if (!old) return;
    if ((old.description || '') !== (f.description || '')) {
      changes.push({ what: 'field', text: `“${f.name.replace(/_/g, ' ')}” instructions changed`, diff: wordDiff(old.description, f.description) });
    }
    if (after.style === 'table' && (old.column !== false) !== (f.column !== false)) {
      changes.push({ what: 'field', text: `“${f.name.replace(/_/g, ' ')}” ${f.column === false ? 'hidden from' : 'shown in'} the table` });
    }
  });
  if (before.shown !== after.shown) changes.push({ what: 'shown', text: after.shown ? 'Now shown in the document' : 'Hidden from the document (still written)' });
  if (before.shown && after.shown && before.inPdf !== after.inPdf) changes.push({ what: 'pdf', text: after.inPdf ? 'Now included in the PDF' : 'Left out of the PDF' });
  return changes;
}

// Sections that moved: everything outside the longest run of sections still in their old order. Swapping two
// neighbours reports only the one that was moved.
function moved(oldOrder, newOrder) {
  const lcs = Array.from({ length: oldOrder.length + 1 }, () => new Array(newOrder.length + 1).fill(0));
  for (let i = oldOrder.length - 1; i >= 0; i -= 1) {
    for (let j = newOrder.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = oldOrder[i] === newOrder[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const kept = new Set();
  let i = 0;
  let j = 0;
  while (i < oldOrder.length && j < newOrder.length) {
    if (oldOrder[i] === newOrder[j]) { kept.add(oldOrder[i]); i += 1; j += 1; }
    else if (lcs[i + 1][j] > lcs[i][j + 1]) i += 1;
    else j += 1;
  }
  return new Set(newOrder.filter((k) => !kept.has(k)));
}

/**
 * Document changes: [{ kind: 'added'|'removed'|'moved'|'changed', key, title, detail?, changes? }], in the new
 * document's order, with removed sections at the end.
 */
export function documentChanges(base, proposal) {
  const before = sectionsFrom(base.result_schema, base.display);
  const after = sectionsFrom(proposal.result_schema, proposal.display);
  const oldByKey = Object.fromEntries(before.map((s) => [s.key, s]));
  const newKeys = new Set(after.map((s) => s.key));
  const oldOrder = before.filter((s) => s.shown && newKeys.has(s.key)).map((s) => s.key);
  const newOrder = after.filter((s) => s.shown && oldByKey[s.key]).map((s) => s.key);
  const movedKeys = moved(oldOrder, newOrder);
  const out = [];
  after.forEach((s, i) => {
    const old = oldByKey[s.key];
    if (!old) {
      const prev = after.slice(0, i).reverse().find((x) => x.shown);
      const what = s.fields.length ? ` · ${s.style === 'table' ? 'columns' : 'fields'}: ${fieldNames(s).join(', ')}` : '';
      out.push({
        kind: 'added', key: s.key, title: title(s),
        detail: `${styleLabel(s.style)}${what}${s.shown ? (prev ? `, after “${title(prev)}”` : ', at the top') : ', hidden'}`,
        changes: s.description ? [{ what: 'instructions', text: `“${excerpt(s.description)}”` }] : [],
      });
      return;
    }
    const changes = sectionChanges(old, s);
    const moved = movedKeys.has(s.key);
    if (moved) {
      const prev = newOrder[newOrder.indexOf(s.key) - 1];
      out.push({ kind: 'moved', key: s.key, title: title(s), detail: prev ? `now after “${title(after.find((x) => x.key === prev))}”` : 'now at the top', changes });
    } else if (changes.length) {
      out.push({ kind: 'changed', key: s.key, title: title(s), changes });
    }
  });
  before.filter((s) => !newKeys.has(s.key)).forEach((s) => out.push({ kind: 'removed', key: s.key, title: title(s) }));
  return out;
}

function paragraphs(text) {
  return String(text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

function headingOf(paragraph) {
  const first = paragraph.split('\n')[0];
  return /^#{1,6}\s/.test(first) ? first.replace(/^#+\s*/, '') : null;
}

/**
 * Prompt changes between two prompts, paragraph by paragraph: [{ kind: 'added'|'removed'|'changed', under, diff,
 * words? }]. `under` is the heading the change sits under, so people can find it. Changed paragraphs get a word-level
 * redline, shown in full.
 */
export function promptChanges(beforePrompt, afterPrompt) {
  const a = paragraphs(beforePrompt);
  const b = paragraphs(afterPrompt);
  const lcs = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out = [];
  let under = null;
  let removed = [];
  let added = [];
  const flush = () => {
    // Pair removed and added paragraphs in order: a pair is an edit, the rest are plain removals or additions.
    const n = Math.min(removed.length, added.length);
    for (let k = 0; k < n; k += 1) {
      out.push({ kind: 'changed', under: headingOf(added[k]) || under, diff: wordDiff(removed[k], added[k]) });
    }
    removed.slice(n).forEach((p) => out.push({ kind: 'removed', under: headingOf(p) || under, diff: [{ op: '-', text: p }], words: wordCount(p) }));
    added.slice(n).forEach((p) => out.push({ kind: 'added', under: headingOf(p) || under, diff: [{ op: '+', text: p }], words: wordCount(p) }));
    removed = [];
    added = [];
  };
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      flush();
      under = headingOf(b[j]) || under;
      i += 1; j += 1;
    } else if (j >= b.length || (i < a.length && lcs[i + 1][j] >= lcs[i][j + 1])) {
      removed.push(a[i]); i += 1;
    } else {
      added.push(b[j]); j += 1;
    }
  }
  flush();
  return out;
}

/** Everything a person needs to read a change between two versions of a document template. */
export function templateChanges(before, after) {
  return {
    document: documentChanges(before, after),
    prompt: promptChanges(before.prompt, after.prompt),
  };
}
